import type { FC } from "react";
import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { SurveyGroupResponse, postOrgSurveyGroup } from "@rubi/api-generated";
import { toast } from "@rubi/ui";

import styles from "./CreateGroupDialog.module.scss";

export interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (group: SurveyGroupResponse) => void;
  orgId: string;
}

const CreateGroupDialog: FC<CreateGroupDialogProps> = ({
  open,
  onClose,
  onCreated,
  orgId,
}) => {
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await postOrgSurveyGroup({
        path: { org_id: orgId },
        body: {
          name: name.trim(),
          icon_name: "",
          sort_order: sortOrder !== "" ? parseInt(sortOrder, 10) : null,
        },
      });
      onCreated(res.data!.group);
      setName("");
      setSortOrder("");
      onClose();
    } catch {
      toast.error("Failed to create group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Survey Group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} className={styles.contentStack}>
          <TextField
            label="Group Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Sort Order"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            type="number"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !name.trim()}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGroupDialog;
